import { IMicrocycle, IPrewod, IManagedExercise, ISet, formFieldNames, IMicrocycleError, IUserFitnessPlan } from "./interface";
import { WORKOUT_PHASES, STATE_VALUES, WORKOUT_FOCUS, WORKOUT_PHASE_TYPES, LEVEL_OPTIONS } from "../constants";
import { get, groupBy, cloneDeep } from "lodash";


export const getInitialWodValues = (selectedDay: number, diffTenantId?: number) => {
    const allSets = WORKOUT_PHASES.map(workoutPhase => getSetValue(workoutPhase, diffTenantId));

    const newPreWod: IPrewod = {
        workoutType: '',
        dayOfCycle: selectedDay,
        sets: allSets
    };
    return newPreWod;
}

export const getSetValue = (workoutPhase: string, diffTenantId?: number): ISet => {
    const managedExercises = getManagedExercise(diffTenantId);
    return ({
        blockType: workoutPhase,
        repetition: undefined,
        setType: undefined,
        tenantId: diffTenantId || 2,
        managedExercises,
        expectedDurationInSeconds: undefined,
        trainerComments: undefined
    })
}

export const getManagedExercise = (diffTenantId?: number) => {
    const managedExerciseInitialValue: IManagedExercise[] = [{
        exerciseId: undefined,
        exerciseType: "EXERCISE",
        tenantId: diffTenantId || 2,
        trainerComments: undefined,
        executions: [{
            duration: undefined,
            type: undefined,
            weight: undefined,
            weightUnit: undefined,
        }]
    }]

    return managedExerciseInitialValue
}


export const createRequestBody = (microcycleData: IMicrocycle, createdBy: number, isDraft: boolean,) => {
    let templateData = cloneDeep(microcycleData);
    templateData.prewods.map(wod => {
        const isSimpleWorkout = wod.workoutType === WORKOUT_FOCUS.SimpleWorkouts;
        wod.tenantId = templateData.tenantId;
        wod.regimenId = templateData.regimenId;
        wod.goalId = templateData.goalId;
        wod.level = templateData.level;
        wod.workoutDays = templateData.workoutDays;
        wod.sets && wod.sets.map((set, setIndex) => {
            let hasAnyValue = true;
            //if simple workout value is not filled delete the set
            if (isSimpleWorkout || isDraft) {
                hasAnyValue = checkForSimpleWorkoutValue(set);
                if (!hasAnyValue) {
                    delete wod.sets[setIndex]
                }
            }

            if (hasAnyValue) {
                set.managedExercises && set.managedExercises.map((exercise, exerciseIndex) => {
                    exercise.sequence = exerciseIndex + 1;
                    exercise.executions && exercise.executions.map(execution => {
                        execution[formFieldNames.TYPE] = "TIMED"
                        if (!checkForEmptyValue(execution.count)) {
                            execution[formFieldNames.TYPE] = "COUNT"
                        }
                        if (!checkForEmptyValue(execution.duration)) {
                            execution[formFieldNames.TYPE] = "TIMED"
                        }
                        if (!checkForEmptyValue(execution.count) && !checkForEmptyValue(execution.duration)) {
                            execution[formFieldNames.TYPE] = "TIMED_COUNT"
                        }
                    })
                })
                set.sequence = setIndex + 1;
            }
        })
        if (wod.sets) {
            wod.sets = wod.sets.filter(Boolean)
        }
    })

    templateData.state = isDraft ? STATE_VALUES.DRAFT : STATE_VALUES.LIVE;
    templateData.createdBy = createdBy ? createdBy : templateData.createdBy;

    return templateData
}



const checkForEmptyValue = (value: any) => {
    return (value === undefined || value === null || value === "");
}

export const validateMicrocycleData = (microcycle: IMicrocycle) => {
    const mandatoryFields: any = [formFieldNames.TEMPLATENAME, formFieldNames.GOALID, formFieldNames.LEVEL, formFieldNames.WORKOUTDAYS]
    const errors: IMicrocycleError[] = [];

    //check for mandatory values for fitness goal basic details
    for (const field in mandatoryFields) {
        const value = (microcycle as any)[mandatoryFields[field]];
        if (checkForEmptyValue(value)) {
            errors.push({
                identifier: "MANDATORY",
                fieldName: mandatoryFields[field]
            })
        }
    }

    microcycle.prewods.forEach((wod, wodIndex) => {
        const workoutFocus = wod.workoutType;
        if (checkForEmptyValue(workoutFocus)) {
            errors.push({
                identifier: "EMPTY_WORKOUT_FOCUS",
                day: wod.dayOfCycle,
            })
        }

        if (workoutFocus != WORKOUT_FOCUS.RestDay) {
            //check for sets with no exercises
            const groupedSets = groupBy(wod.sets, (item) => item.blockType);

            const isSimpleWorkout = workoutFocus === WORKOUT_FOCUS.SimpleWorkouts; //for simple workout type atleast main workout should be present

            Object.keys(groupedSets).map((set) => {
                groupedSets[set].map((workout, workoutIndex) => {
                    const workoutPhase = workout.blockType;
                    let hasAnyValueFilled: boolean = false;

                    // check if focus is simple but has some values in other two phases apart from main workout
                    if (isSimpleWorkout && workoutPhase !== WORKOUT_PHASE_TYPES.WORKOUT) {
                        hasAnyValueFilled = checkForSimpleWorkoutValue(workout);
                    }

                    //if workout focus is simple workout or phase is main workout.
                    if ((isSimpleWorkout && workoutPhase === WORKOUT_PHASE_TYPES.WORKOUT) || (!isSimpleWorkout) || hasAnyValueFilled) {
                        //either of Duration or Rounds should be there
                        if (checkForEmptyValue(workout.repetition) && checkForEmptyValue(workout.expectedDurationInSeconds)) {
                            errors.push({
                                identifier: "EITHER_OF_ONE_IN_WORKOUT_PHASE",
                                day: wod.dayOfCycle,
                                phase: set,
                                workoutIndex,
                                message: "Please enter either Duration or Rounds"
                            })
                        }

                        if (checkForEmptyValue(workout.setType)) {
                            errors.push({
                                identifier: "MANDATORY",
                                fieldName: formFieldNames.SETTYPE,
                                day: wod.dayOfCycle,
                                phase: set,
                                workoutIndex
                            })
                        }

                        workout.managedExercises.map((exercise, exerciseIndex) => {
                            //check for exercise Id
                            if (checkForEmptyValue(exercise.exerciseId)) {
                                errors.push({
                                    identifier: "EMPTY_EXERCISE",
                                    day: wod.dayOfCycle,
                                    phase: set,
                                    workoutIndex,
                                    exerciseIndex
                                })
                            }

                            exercise.executions && exercise.executions.map((execution, executionIndex) => {
                                if (checkForEmptyValue(execution.duration) && checkForEmptyValue(execution.count)) {
                                    errors.push({
                                        identifier: "EITHER_OF_ONE_EXERCISE_SET_INFO",
                                        day: wod.dayOfCycle,
                                        phase: set,
                                        workoutIndex,
                                        message: "Please enter either Reps or Time",
                                        exerciseIndex
                                    })

                                }
                            })
                        })
                    }
                })
            })
        }
    })

    return errors;
}

const checkForSimpleWorkoutValue = (workout: ISet) => {
    let hasAnyValueFilled: boolean = false;

    if (workout.expectedDurationInSeconds || workout.repetition || workout.trainerComments || workout.setType) {
        hasAnyValueFilled = true;
    }
    //check for data in managed exercises and executions
    workout.managedExercises && workout.managedExercises.find(exercise => {
        if (exercise.exerciseId || exercise.trainerComments) {
            hasAnyValueFilled = true;
        }
        exercise.executions && exercise.executions.map(execution => {
            if (execution.count || execution.duration || execution.weight) {
                hasAnyValueFilled = true;
            }

        })
    })

    return hasAnyValueFilled;
}

export const checkForError = (errorsList: IMicrocycleError[], errorIdentifier?: string, field?: string) => {
    if (errorIdentifier) {
        return errorsList && errorsList.find(({ identifier }) => identifier === errorIdentifier) ? true : false
    }
    if (field) {
        return errorsList && errorsList.find(({ fieldName }) => fieldName === field) ? true : false
    }
}


export const formatUserPlanToFitnessTemplateObject = (userMicrocycle: IUserFitnessPlan, managedMicrocycle: IMicrocycle) => {
    const { microCycleEntry, userGoalEntry } = userMicrocycle;

    const wods = microCycleEntry.userWods.map(wod => {
        wod[formFieldNames.WORKOUTTYPE] = wod.workoutName;
        wod.sets.map(set => set.managedExercises = set.exercises)
        return wod;
    })

    const microcyle: IMicrocycle = {
        tenantId: userGoalEntry.tenant.id,
        regimenId: userGoalEntry.regimen.id,
        goalId: userGoalEntry.goal.id,
        level: managedMicrocycle.level,
        workoutDays: userGoalEntry.workoutsPerWeek,
        description: microCycleEntry.description,
        prewods: wods,
        meta: {
            gender: get(managedMicrocycle, "meta.gender", null)
        }
    }

    return microcyle;
}

export const formatFitnessTemplateToUserPlanObject = (microcycleTemplate: IMicrocycle, userMicrocyle: IUserFitnessPlan) => {
    const { microCycleEntry, userGoalEntry } = userMicrocyle;
    //add level and gender
    const goalData = {
        ...userMicrocyle.userGoalEntry,
        goal: { ...userGoalEntry.goal, id: microcycleTemplate.goalId },
        regimen: { ...userGoalEntry.regimen, id: microcycleTemplate.regimenId },
        tenant: { ...userGoalEntry.tenant, id: microcycleTemplate.tenantId },
        workoutsPerWeek: microcycleTemplate.workoutDays
    }

    const wods: any[] = microcycleTemplate.prewods.map(wod => {
        wod.workoutName = wod[formFieldNames.WORKOUTTYPE];
        wod.sets.map(set => {
            set.exercises = set.managedExercises;
            set.exercises.map(exercise => {
                exercise.userId = microCycleEntry.userId;
                exercise.status = "CREATED";
                return exercise;
            });
            set.status = "CREATED";
            set.repetition = set.repetition ? set.repetition : 1;
            delete set.managedPreWodId;
            delete set.managedExercises;
            return set;
        });
        return wod;
    })

    const microCycleData = {
        ...microCycleEntry,
        description: microcycleTemplate.description,
        userWods: wods
    }
    return { microCycleEntry: microCycleData, userGoalEntry: goalData };
}

export const validateMicrocycleinDraftState = (microcycle: IMicrocycle) => {
    const mandatoryFields: any = [formFieldNames.TEMPLATENAME, formFieldNames.LEVEL, formFieldNames.WORKOUTDAYS]
    const errors: IMicrocycleError[] = [];

    //check for mandatory values for fitness goal basic details
    for (const field in mandatoryFields) {
        const value = (microcycle as any)[mandatoryFields[field]];
        if (checkForEmptyValue(value)) {
            errors.push({
                identifier: "MANDATORY",
                fieldName: mandatoryFields[field]
            })
        }
    }

    return errors
}